import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DownloadTicketsMenu from 'app/pages/OrderConfirmationPage/DownloadTicketsMenu';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventFieldType } from 'types/EventField';
import { DateTimeFormat } from 'types/EventState';
import { onResize } from 'utils/embed';
import { formatDateAndTimeIntervalOnTimeZone } from 'utils/events';
import AddonsAndTopUpsSection from '../AddonsAndTopUpsSection/AddonsAndTopUpsSection';
import { OveitIcon } from '../OveitIcon';
import { DisplayedTicketRegistrationForms } from '../ParticipantsInformation/ParticipantsInformation';
import ShareTicketDialog from '../ShareTicketDialog/ShareTicketDialog';
import AddTicketToAppleWallet from 'app/pages/OrderConfirmationPage/AddTicketToAppleWallet';

interface RowProps {
  ticket: DisplayedTicketRegistrationForms;
  orderHasAddonsOrTopUp: boolean;
  dateTimeFormat: DateTimeFormat;
  displayedRegistrationFields: {
    id: string;
    label: string;
  }[];
  eventId: string;
  eventRegistrationForm: EventFieldType[];
}

const Row: React.FC<RowProps> = ({
  ticket: {
    name,
    series,
    id_order_item,
    index,
    download,
    addons,
    wallet_credit,
    dateTime,
    registration_form,
    seat,
  },
  orderHasAddonsOrTopUp,
  dateTimeFormat,
  displayedRegistrationFields,
  eventId,
  eventRegistrationForm,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ticketHasAddonsOrTopUp = orderHasAddonsOrTopUp
    ? (addons && addons.length > 0) ||
      (wallet_credit && Object.keys(wallet_credit).length > 0)
    : false;

  const parseRegistrationFormValueToSomethingThatCanBeDisplayed = (
    value,
    fieldId,
  ) => {
    if (Array.isArray(value)) return parseSelectFieldValue(value, fieldId);
    if (typeof value === 'boolean')
      return value ? <OveitIcon name="solidGreenCheck" /> : '-';
    return value;
  };

  const parseSelectFieldValue = (values: string[], fieldId: string) => {
    const result: string[] = [];

    values.forEach(value => {
      const field = eventRegistrationForm.find(field => field.id === fieldId);
      if (field) {
        const options = field.meta.options;
        result.push(options[value]);
      }
    });

    return result.join(', ');
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset !important' } }}>
        {orderHasAddonsOrTopUp ? (
          <TableCell sx={{ padding: 0, width: 'fit-content' }}>
            {ticketHasAddonsOrTopUp ? (
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            ) : null}
          </TableCell>
        ) : null}
        <TableCell
          sx={{
            paddingInline: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {displayedRegistrationFields.length > 0 ? (
            <ShareTicketDialog
              idOrderItem={id_order_item}
              formIndex={index}
              eventId={eventId}
              buttonText={t('common.share')}
            />
          ) : null}
          <AddTicketToAppleWallet link={download.pkpass} />
          <DownloadTicketsMenu download={download} />
        </TableCell>
        <TableCell scope="row" sx={{ minWidth: 160 }}>
          <p className="p1-medium"> {name}</p>
          {dateTime?.timeStart && dateTime?.timeEnd ? (
            <p className="p4-regular text-neutral-800">
              (
              {formatDateAndTimeIntervalOnTimeZone(
                dateTimeFormat.timezone,
                dateTimeFormat.dateFormat,
                dateTimeFormat.timeFormat,
                new Date(
                  dateTime.date + ' ' + dateTime.timeStart,
                ).toISOString(),
                new Date(dateTime.date + ' ' + dateTime.timeEnd).toISOString(),
                t('common.at'),
              )}
              )
            </p>
          ) : null}
        </TableCell>
        {displayedRegistrationFields.length > 0 ? (
          displayedRegistrationFields.map(field => (
            <TableCell key={field.id} sx={{ minWidth: 100 }}>
              <p className="p1-medium">
                {parseRegistrationFormValueToSomethingThatCanBeDisplayed(
                  registration_form?.registrationForm[field.id] || '-',
                  field.id,
                )}
              </p>
            </TableCell>
          ))
        ) : (
          <TableCell sx={{ minWidth: 100 }}>
            <p className="p1-medium"> {seat || series}</p>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingTop: 0, paddingBottom: 0 }} colSpan={12}>
          <Collapse
            onEntered={onResize}
            onExited={onResize}
            in={open}
            timeout="auto"
            unmountOnExit
          >
            <div className="pb-[16px]">
              <AddonsAndTopUpsSection
                addons={addons}
                wallet_credit={wallet_credit}
              />
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface TicketsSectionOrderConfirmationPageProps {
  tickets: DisplayedTicketRegistrationForms[];
  dateTimeFormat: DateTimeFormat;
  registrationForms: EventFieldType[];
  eventId: string;
  hasSeatingChart: boolean;
  eventRegistrationForm: EventFieldType[];
}

const TicketsSectionOrderConfirmationPage: React.FC<
  TicketsSectionOrderConfirmationPageProps
> = ({
  tickets,
  dateTimeFormat,
  registrationForms,
  eventId,
  hasSeatingChart,
  eventRegistrationForm,
}) => {
  const { t } = useTranslation();
  // check if there is any ticket with addons or top-up
  const orderHasAddonsOrTopUp = tickets.find(
    ({ addons, wallet_credit }) =>
      (addons && addons.length > 0) ||
      (wallet_credit && Object.keys(wallet_credit).length > 0),
  );

  const displayedRegistrationFields = [...registrationForms]
    .splice(0, 3)
    .map(({ id, label }) => ({
      id,
      label,
    }));

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            {orderHasAddonsOrTopUp ? <TableCell /> : null}
            <TableCell>
              <p className="p3-medium">
                {t('orderConfirmationPage.ticketActions')}
              </p>
            </TableCell>
            <TableCell>
              <p className="p3-medium">
                {t('orderConfirmationPage.ticketCategory')}
              </p>
            </TableCell>
            {displayedRegistrationFields.length > 0 ? (
              displayedRegistrationFields.map(form => (
                <TableCell key={form.id}>
                  <p className="p3-medium">{form.label}</p>
                </TableCell>
              ))
            ) : (
              <TableCell>
                <p className="p3-medium">
                  {hasSeatingChart
                    ? t('orderConfirmationPage.ticketSeats')
                    : t('orderConfirmationPage.ticketSeries')}
                </p>
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody
          sx={{ '& tr:last-of-type td': { borderBottom: 'unset !important' } }}
        >
          {tickets.map(ticket => (
            <Row
              key={`${ticket.ticket_type_id}-${ticket.index}`}
              ticket={ticket}
              orderHasAddonsOrTopUp={!!orderHasAddonsOrTopUp}
              dateTimeFormat={dateTimeFormat}
              displayedRegistrationFields={displayedRegistrationFields}
              eventId={eventId}
              eventRegistrationForm={eventRegistrationForm}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TicketsSectionOrderConfirmationPage;
